import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Picks a single "effective" role from a list, prioritizing admin routes.
 */
function pickEffectiveRole(roles: string[] | null | undefined) {
  if (!roles || roles.length === 0) return null;
  const normalized = roles.map((x) => {
    const role = String(x).toLowerCase();
    return role === "super_admin" ? "admin" : role;
  });
  if (normalized.includes("admin")) return "admin";
  if (normalized.includes("client_admin")) return "client_admin";
  if (normalized.includes("portfolio_manager")) return "portfolio_manager";
  if (normalized.includes("org_admin")) return "org_admin";
  if (normalized.includes("master_reviewer")) return "master_reviewer";
  if (normalized.includes("reviewer")) return "reviewer";
  if (normalized.includes("respondent")) return "respondent";
  return normalized[0];
}

export function useEffectiveRole() {
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    // Ensure role is fetched exactly once per mount
    if (hasFetched.current) return;
    hasFetched.current = true;

    let cancelled = false;

    (async () => {
      setError(null);

      // Check for session first
      const { data: sess } = await supabase.auth.getSession();
      const uid = sess?.session?.user?.id;

      // If not logged in, do NOT query roles tables
      if (!uid) {
        if (!cancelled) {
          setRole(null);
          setLoading(false);
        }
        return;
      }

      let userRoles: string[] = [];

      // Use RPC only (bypasses RLS via SECURITY DEFINER)
      const { data: rpcRows, error: rpcErr } = await supabase.rpc("current_user_roles");
      if (rpcErr) {
        console.warn("[useEffectiveRole] current_user_roles RPC failed:", rpcErr);
      } else {
        userRoles = Array.isArray(rpcRows)
          ? rpcRows.map((r: any) => (typeof r === "string" ? r : r.role)).filter(Boolean)
          : [];
      }

      // Also get sub_org_memberships for PM role via RPC
      const { data: somRpcRows, error: somRpcErr } = await supabase.rpc("current_user_sub_org_roles");
      if (somRpcErr) {
        console.warn("[useEffectiveRole] current_user_sub_org_roles RPC failed:", somRpcErr);
      } else {
        const somRoles = Array.isArray(somRpcRows)
          ? somRpcRows.map((r: any) => (typeof r === "string" ? r : r.role)).filter(Boolean)
          : [];
        userRoles = [...userRoles, ...somRoles];
      }

      const uniqueRoles = Array.from(new Set(userRoles));
      const effective = pickEffectiveRole(uniqueRoles);

      console.log("[useEffectiveRole] resolved:", { uniqueRoles, effective });

      if (!cancelled) {
        setRole(effective);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { role, loading, error };
}
